'use client'

import {
  DRIVER_DISPLAY_IDENTIFIER_OPTIONS,
  getAvailableDriverDisplayIdentifierOptions,
  getDefaultDriverDisplayIdentifier,
  getInitialDriverDisplayIdentifier,
  isDriverDisplayIdentifierAvailableForSource,
} from '@/lib/driver-display-identifiers'
import { useCallback, useEffect, useMemo, useState } from 'react'

function getFallbackDriverDisplayIdentifierOptions({
  source,
  context,
  defaultIdentifier,
}) {
  if (context !== 'standings') {
    return []
  }

  const preferredIdentifier =
    defaultIdentifier ||
    getDefaultDriverDisplayIdentifier({
      source,
      context,
    })

  const availableForSource = DRIVER_DISPLAY_IDENTIFIER_OPTIONS.filter(
    (option) =>
      isDriverDisplayIdentifierAvailableForSource(option.value, source),
  )
  const preferredOption = availableForSource.find(
    (option) => option.value === preferredIdentifier,
  )

  return preferredOption ? [preferredOption] : availableForSource.slice(0, 1)
}

export default function useDriverDisplayIdentifier({
  items = [],
  source,
  context = 'import',
  defaultIdentifier,
}) {
  const availableDriverDisplayIdentifierOptions = useMemo(
    () => getAvailableDriverDisplayIdentifierOptions(items, source),
    [items, source],
  )
  const fallbackDriverDisplayIdentifierOptions = useMemo(
    () =>
      getFallbackDriverDisplayIdentifierOptions({
        source,
        context,
        defaultIdentifier,
      }),
    [context, defaultIdentifier, source],
  )
  const initialDriverDisplayIdentifierOptions =
    availableDriverDisplayIdentifierOptions.length > 0
      ? availableDriverDisplayIdentifierOptions
      : fallbackDriverDisplayIdentifierOptions

  const initialIdentifier = useMemo(
    () =>
      getInitialDriverDisplayIdentifier({
        items,
        source,
        context,
        defaultIdentifier,
      }),
    [context, defaultIdentifier, items, source],
  )

  const [driverDisplayIdentifier, setDriverDisplayIdentifier] =
    useState(initialIdentifier)
  const [driverDisplayIdentifierOptions, setDriverDisplayIdentifierOptions] =
    useState(initialDriverDisplayIdentifierOptions)
  const [hasManuallyChangedIdentifier, setHasManuallyChangedIdentifier] =
    useState(false)

  useEffect(() => {
    if (availableDriverDisplayIdentifierOptions.length > 0) {
      setDriverDisplayIdentifierOptions(availableDriverDisplayIdentifierOptions)
      return
    }

    setDriverDisplayIdentifierOptions((currentOptions) =>
      currentOptions.length > 0
        ? currentOptions
        : fallbackDriverDisplayIdentifierOptions,
    )
  }, [
    availableDriverDisplayIdentifierOptions,
    fallbackDriverDisplayIdentifierOptions,
  ])

  useEffect(() => {
    if (!hasManuallyChangedIdentifier) {
      setDriverDisplayIdentifier(initialIdentifier)
      return
    }

    if (
      driverDisplayIdentifierOptions.some(
        (option) => option.value === driverDisplayIdentifier,
      )
    ) {
      return
    }

    setDriverDisplayIdentifier(initialIdentifier)
  }, [
    driverDisplayIdentifier,
    driverDisplayIdentifierOptions,
    hasManuallyChangedIdentifier,
    initialIdentifier,
  ])

  const handleDriverDisplayIdentifierChange = useCallback((value) => {
    setHasManuallyChangedIdentifier(true)
    setDriverDisplayIdentifier(value)
  }, [])

  return {
    driverDisplayIdentifier,
    driverDisplayIdentifierOptions,
    setDriverDisplayIdentifier: handleDriverDisplayIdentifierChange,
  }
}
